import { EXCHANGES } from "@cede/utils";
import { useBinanceOrderBook } from "./binance";
import { useBinanceFuturesOrderBook } from "./binance-futures/useBinanceFuturesOrderBook";
import { useBitgetOrderBook } from "./bitget";
import { useBitmartOrderBook } from "./bitmart";
import { useBitstampOrderBook } from "./bitstamp";
import { useBybitOrderBook } from "./bybit";
import { useCoinbaseOrderBook } from "./coinbase";
import { isDirectWSSupported } from "./constants";
import { useCryptocomOrderBook } from "./cryptocom";
import { useGateIOOrderBook } from "./gateio";
import { useHTXOrderBook } from "./htx";
import { useKatanaOrderBook } from "./katana";
import { useKrakenOrderBook } from "./kraken";
import { useKrakenFuturesOrderBook } from "./krakenfutures";
import { useOKXOrderBook } from "./okx";
import type { UseDirectOrderBookWSResult } from "./types";

interface UseExchangeOrderBookProps {
  exchangeId: string;
  pairSymbol: string;
  /**
   * only used for krakenfutures
   */
  externalMarketId: string;
  enabled?: boolean;
}

/**
 * Unified hook selector for direct WebSocket orderbook connections
 *
 * Returns the appropriate exchange-specific hook based on exchangeId.
 * Returns null for unsupported exchanges (fallback to Kenobi SSE in component).
 *
 * Note: All hooks are called unconditionally to comply with React hooks rules.
 * Each hook is passed enabled=false for non-matching exchanges.
 */
export function useExchangeOrderBook({
  exchangeId,
  pairSymbol,
  externalMarketId,
  enabled = true,
}: UseExchangeOrderBookProps): UseDirectOrderBookWSResult | null {
  // Perps pairs carry a ":settlement" suffix (e.g. BTC/USDT:USDT); spot pairs don't.
  const isPerpsPair = pairSymbol.includes(":");

  // Call all hooks unconditionally (React rules of hooks)
  // Each hook is disabled unless it matches the current exchangeId
  // Binance spot (stream.binance.com) and USDⓈ-M perps (fstream.binance.com) share the
  // `binance` id but hit different WS endpoints — route by pair shape.
  const binance = useBinanceOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.BINANCE && !isPerpsPair,
  });

  const binanceFutures = useBinanceFuturesOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.BINANCE && isPerpsPair,
  });

  const okx = useOKXOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.OKX,
  });

  const gateio = useGateIOOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.GATEIO,
  });

  const bitget = useBitgetOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.BITGET,
  });

  const bitmart = useBitmartOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.BITMART,
  });

  const bitstamp = useBitstampOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.BITSTAMP,
  });

  const bybit = useBybitOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.BYBIT,
  });

  const coinbase = useCoinbaseOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.COINBASE,
  });

  const htx = useHTXOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.HTX,
  });

  const kraken = useKrakenOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.KRAKEN,
  });

  const krakenfutures = useKrakenFuturesOrderBook({
    pairSymbol,
    externalMarketId,
    enabled: enabled && exchangeId === EXCHANGES.KRAKEN_FUTURES,
  });

  const cryptocom = useCryptocomOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.CRYPTOCOM,
  });

  const katana = useKatanaOrderBook({
    pairSymbol,
    enabled: enabled && exchangeId === EXCHANGES.KATANA,
  });

  // Return the matching hook result, or null for unsupported exchanges
  if (!isDirectWSSupported(exchangeId)) {
    return null;
  }

  switch (exchangeId) {
    case EXCHANGES.BINANCE:
      return isPerpsPair ? binanceFutures : binance;
    case EXCHANGES.OKX:
      return okx;
    case EXCHANGES.GATEIO:
      return gateio;
    case EXCHANGES.BITGET:
      return bitget;
    case EXCHANGES.BITMART:
      return bitmart;
    case EXCHANGES.BITSTAMP:
      return bitstamp;
    case EXCHANGES.BYBIT:
      return bybit;
    case EXCHANGES.COINBASE:
      return coinbase;
    case EXCHANGES.HTX:
      return htx;
    case EXCHANGES.KRAKEN:
      return kraken;
    case EXCHANGES.KRAKEN_FUTURES:
      return krakenfutures;
    case EXCHANGES.CRYPTOCOM:
      return cryptocom;
    case EXCHANGES.KATANA:
      return katana;
    default:
      return null;
  }
}
